import { FC } from 'react'
import {
  countriesAlpha2Emojis,
  State,
  toCountryLabel,
  useLoadAllItemsQuery,
  statesWithNamesForCountry
} from '@sequencehq/api'
import {
  Text,
  Badge,
  Button,
  EmptyState,
  GreyGrey80,
  IconPlus,
  IconReceiptTax,
  IndigoIndigo60,
  Link,
  Primary14SemiBold,
  SequenceMenu,
  SequenceMenuItemProps,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  Flex
} from '@sequencehq/core-components'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { TaxCategoryModal } from 'SettingsV2/components/tax-rates/modals/TaxCategoryModal'
import { useCreateTaxCategory } from 'SettingsV2/components/tax-rates/hooks/useCreateTaxCategory'
import { useUpdateTaxCategory } from 'SettingsV2/components/tax-rates/hooks/useUpdateTaxCategory'
import { useMarkTaxCategoryAsDefault } from 'SettingsV2/components/tax-rates/hooks/useMarkTaxCategoryAsDefault'
import { useCreateTaxRate } from 'SettingsV2/components/tax-rates/hooks/useCreateTaxRate'
import { useUpdateTaxRate } from 'SettingsV2/components/tax-rates/hooks/useUpdateTaxRate'
import { TaxRatesModal } from 'SettingsV2/components/tax-rates/modals/TaxRatesModal'
import {
  getTaxRateDescription,
  getTaxRatesWithCategories
} from 'SettingsV2/components/tax-rates/taxRates.utils'
import { SettingsPageLayout } from '../SettingsPageLayout'

export const SalesTaxSettings: FC = () => {
  const taxCategoriesQuery = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxCategories
  )

  const taxRatesQuery = useLoadAllItemsQuery(
    dashboardv99990101Client.getTaxRates
  )

  const createTaxCategory = useCreateTaxCategory()
  const editTaxCategory = useUpdateTaxCategory()
  const markTaxCategoryAsDefault = useMarkTaxCategoryAsDefault()
  const createTaxRate = useCreateTaxRate()
  const updateTaxRate = useUpdateTaxRate()

  const taxCategories =
    taxCategoriesQuery.data?.pages.flatMap(page => page.items) ?? []

  // Temporary measure to map tax category name to every tax rate
  // will be removed once backend provides tax category names on the tax rate object
  const taxRates = (
    taxRatesQuery.data?.pages.flatMap(page => page.items) ?? []
  ).map(taxRate => ({
    ...taxRate,
    taxCategory: taxCategories.find(
      category => taxRate.taxCategoryId === category.id
    )!
  }))

  const { groupedByKey, groupedByCountry } = getTaxRatesWithCategories(taxRates)

  return (
    <SettingsPageLayout
      header="Sales tax"
      subheader="Manage tax categories and rates."
      isError={taxCategoriesQuery.isError || taxRatesQuery.isError}
    >
      <SettingsGroup
        isLoading={taxCategoriesQuery.isLoading}
        id="tax-categories"
        action={
          <Flex
            marginBottom="8px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text
              as="h3"
              id="tax-categories"
              {...Primary14SemiBold}
              color={GreyGrey80}
            >
              Tax categories
            </Text>

            <TaxCategoryModal
              onSubmit={createTaxCategory}
              trigger={
                <Button
                  variant="ghost"
                  leadingIcon={<IconPlus color="inherit" />}
                  data-testid="taxCategoryModal.trigger"
                >
                  Add category
                </Button>
              }
            />
          </Flex>
        }
      >
        {taxCategories.length === 0 && (
          <EmptyState
            title="No tax categories"
            icon={IconReceiptTax}
            iconSize="sm"
            body={
              <>
                You have not added any tax categories yet. Add a tax category,{' '}
                <Link
                  _hover={{ textDecoration: 'none' }}
                  to="https://docs.sequencehq.com/tax/manage-sales-tax"
                  color={IndigoIndigo60}
                  target="_blank"
                >
                  learn more
                </Link>
                .
              </>
            }
          />
        )}
        {taxCategories.map(category => (
          <SettingsFormControl
            key={category.id}
            label={category.name}
            helper={category.description}
            htmlFor={`tax-category-${category.id}`}
            icon={
              <SettingsFormControlIcon>
                <IconReceiptTax />
              </SettingsFormControlIcon>
            }
          >
            <Flex gap="8px">
              {category.isDefault && (
                <Badge sentiment="neutral" size="sm">
                  Default
                </Badge>
              )}
              <SequenceMenu
                variant="ghost"
                buttonSize="small"
                data-testid={`taxCategory.${category.id}.menu`}
                items={[
                  (props: Partial<SequenceMenuItemProps>) => (
                    <TaxCategoryModal
                      key="edit"
                      taxCategory={category}
                      trigger={
                        <SequenceMenu.Item
                          uuid="edit"
                          label="Edit"
                          data-testid={`taxCategory.${category.id}.edit`}
                        />
                      }
                      onSubmit={async values => {
                        await editTaxCategory(category, values)
                        props.setIsOpen?.(false)
                      }}
                    />
                  ),
                  {
                    label: 'Mark as default',
                    onClick: () => {
                      markTaxCategoryAsDefault(category.id)
                    }
                  }
                ]}
              />
            </Flex>
          </SettingsFormControl>
        ))}
      </SettingsGroup>

      <SettingsGroup
        isLoading={taxRatesQuery.isLoading}
        action={
          <Flex
            marginBottom="8px"
            alignItems="center"
            justifyContent="space-between"
          >
            <Text
              as="h3"
              id="tax-rates"
              {...Primary14SemiBold}
              color={GreyGrey80}
            >
              Tax rates
            </Text>

            <TaxRatesModal
              data-testid="taxRatesModal"
              onSubmit={createTaxRate}
              countriesWithTaxRates={groupedByCountry}
              trigger={
                <Button
                  data-testid="taxRates.addRate"
                  variant="ghost"
                  leadingIcon={<IconPlus color="inherit" />}
                >
                  Add rate
                </Button>
              }
            />
          </Flex>
        }
      >
        {Object.entries(groupedByKey).map(([groupingKey, allTaxRates]) => {
          const [taxRate] = allTaxRates
          const regionStates = statesWithNamesForCountry(taxRate.country)
          const regionLabel =
            taxRate.region &&
            regionStates[taxRate.region as keyof typeof regionStates]
          return (
            <SettingsFormControl
              key={groupingKey}
              label={`${toCountryLabel(taxRate.country)}${regionLabel ? ` - ${regionLabel}` : ''}`}
              helper={getTaxRateDescription(allTaxRates)}
              htmlFor={`tax-rate-${groupingKey}`}
              icon={
                <SettingsFormControlIcon>
                  {countriesAlpha2Emojis[taxRate.country]}
                </SettingsFormControlIcon>
              }
            >
              <SequenceMenu
                variant="ghost"
                buttonSize="small"
                items={[
                  (props: Partial<SequenceMenuItemProps>) => (
                    <TaxRatesModal
                      key="edit"
                      onSubmit={async (
                        values,
                        taxRatesForCountry,
                        selectedState
                      ) => {
                        void (await updateTaxRate?.(
                          values,
                          taxRatesForCountry,
                          selectedState as State
                        ))
                        props.setIsOpen?.(false)
                      }}
                      options={{
                        closeOnCancel: true,
                        closeOnSubmit: true,
                        onClose: () => {
                          props.setIsOpen?.(false)
                        },
                        onCancel: () => {
                          props.setIsOpen?.(false)
                        }
                      }}
                      countriesWithTaxRates={groupedByCountry}
                      preselectedCountry={taxRate.country}
                      preselectedState={taxRate.region as State}
                      trigger={openModal => (
                        <SequenceMenu.Item
                          uuid="edit"
                          onClick={() => {
                            openModal()
                          }}
                          label="Edit"
                        />
                      )}
                    />
                  )
                ]}
              />
            </SettingsFormControl>
          )
        })}

        {Object.keys(groupedByKey).length === 0 && (
          <EmptyState
            title="No tax rates"
            icon={IconReceiptTax}
            iconSize="sm"
            body={
              <>
                You have not added any tax rates yet. Add a rate for a region
                and tax category,{' '}
                <Link
                  _hover={{ textDecoration: 'none' }}
                  to="https://docs.sequencehq.com/tax/manage-sales-tax"
                  color={IndigoIndigo60}
                  target="_blank"
                >
                  learn more
                </Link>
                .
              </>
            }
          />
        )}
      </SettingsGroup>
    </SettingsPageLayout>
  )
}
